import React, { useContext } from 'react';
import { Image, Row, Col, Placeholder } from '@thd-olt-component-react/core-ui';
import { bool, oneOf, string } from 'prop-types';
import classNames from 'classnames/bind';
import { StorePodContext } from '../../StorePod.context';
import styles from './store-pod-phone-number.module.scss';

const cx = classNames.bind(styles);

export const StorePodPhoneNumber = ({
  boldLabel,
  className,
  colorOfLabel,
  colorOfNumber,
  displayMultiline,
  hideLabel,
  noPadding,
  type,
  showIcon
}) => {
  const { isDesignStore, loading, store } = useContext(StorePodContext);
  const { toolRental: hasToolRentalService } = store?.services || {};

  const isPro = type === 'pro';
  const isStore = type === 'store';
  const isTool = type === 'tool';
  const isServices = type === 'services';

  const unavailableToolRental = isTool && !hasToolRentalService;
  const invalidDesignStoreService = isDesignStore && (isTool || isPro);
  const shouldHidePhone = unavailableToolRental || invalidDesignStoreService;

  const phoneMap = {
    pro: {
      icon: 'pro-icon.svg',
      label: 'Pro Service Desk',
      number: store?.proDeskPhone
    },
    services: {
      icon: null,
      label: 'Home Services',
      number: store?.storeServicesPhone
    },
    store: {
      icon: null,
      label: isDesignStore ? 'Design Center' : 'Store',
      number: store?.phone
    },
    tool: {
      icon: 'rental-icon.svg',
      label: 'Tool & Truck Rental',
      number: store?.toolRentalPhone
    }
  };

  const { icon, label, number } = phoneMap[type];

  const iconClasses = cx('store-pod-phone-number__icon', {
    'store-pod-phone-number__icon--align-to-baseline': showIcon && displayMultiline
  });

  const labelClasses = cx({
    'store-pod-phone-number__label': displayMultiline,
    'store-pod-phone-number__label--bold': boldLabel,
    'store-pod-phone-number__label--alternate': colorOfLabel === 'alternate'
  });

  const linkClasses = cx({
    'store-pod-phone-number__link--alternate': colorOfNumber === 'alternate'
  });

  const phoneNumberClasses = cx(className, 'store-pod-phone-number', {
    'store-pod-phone-number--loading': loading,
    'store-pod-phone-number--padding': !noPadding
  });

  const numberOfRows = displayMultiline ? 2 : 1;
  const heightOfLoader = displayMultiline ? 42 : 21;

  if (shouldHidePhone) return null;

  if (loading) {
    return (
      <div className={phoneNumberClasses}>
        <Placeholder height="14px" type="text" />
        {displayMultiline && (
          <Placeholder height="14px" type="text" />
        )}
      </div>
    );
  }

  return (
    <div className={phoneNumberClasses} data-testid="store-pod-phone-number">
      {showIcon && (
        <Image
          asset={icon}
          alt={label}
          className={iconClasses}
          height="50"
          width="50"
        />
      )}
      <div className={cx('store-pod-phone-number__wrapper')}>
        {!hideLabel && (
          <span className={labelClasses}>
            {label}:&nbsp;
          </span>
        )}
        <a
          className={linkClasses}
          href={'tel:' + number}
          target="_parent"
        >
          {number}
        </a>
      </div>
    </div>
  );
};

StorePodPhoneNumber.propTypes = {
  boldLabel: bool,
  className: string,
  colorOfLabel: oneOf(['alternate', 'dark']),
  colorOfNumber: oneOf(['alternate', 'dark']),
  displayMultiline: bool,
  hideLabel: bool,
  noPadding: bool,
  type: oneOf(['tool', 'store', 'pro', 'services']),
  showIcon: bool
};

StorePodPhoneNumber.defaultProps = {
  boldLabel: false,
  className: null,
  colorOfLabel: 'dark',
  colorOfNumber: 'dark',
  displayMultiline: false,
  hideLabel: false,
  noPadding: false,
  type: 'store',
  showIcon: false
};

StorePodPhoneNumber.displayName = 'StorePodPhoneNumber';
